import { format } from "date-fns";
import { LocalDateString, Workday } from "@lib/store/src/data/timeslots/apiTypes";
import { P } from "@quarks";
import { useMemoDate } from "@lib/store/src/ui/hooks/useMemoDate";
import { cn } from "@utils";
import { extractWorkdayStatus } from "../../utils";
import { WorkdayIcon } from "@molecules";
import { useConnections } from "src/components/pages/Connections/ConnectionsContext/ConnectionsContext";

export const DayItem = ({ dayKey, workdaysForDay }: { dayKey: LocalDateString; workdaysForDay: Workday[] }) => {
  const { showWeekends, selectedDate, setSelectedDate, compactView, canShowFullContent } = useConnections();
  const date = useMemoDate(dayKey);

  const formatString = [
    [showWeekends && compactView, "cccccc"],
    [!showWeekends && compactView, "cccccc"],
    [!showWeekends && !canShowFullContent, "EEEE"],
  ] as const;
  const selectedFormat = (formatString.find(([condition]) => condition)?.[1] as string) ?? "EEE";

  const dayName = format(date, selectedFormat);
  const dayNumber = format(date, canShowFullContent || showWeekends ? "d" : "d MMMM");

  const workdayStatus = extractWorkdayStatus(workdaysForDay);

  return (
    <button
      className={cn(
        "flex flex-col gap-2 items-center sm:items-start leading-4 relative overflow-y-clip isolate",
        "[&:hover>[data-indicator]]:bg-mapiq-black-800*/[0.03]",
      )}
      onClick={() => setSelectedDate(dayKey)}>
      <div className="grid gap-1 text-center sm:text-left">
        <small className="text-mapiq-black-500">{dayName}</small>
        <P
          fontWeight="bold"
          className="text-nowrap">
          {dayNumber}
        </P>{" "}
      </div>

      <WorkdayIcon
        status={workdayStatus}
        className={cn(
          "flex items-center justify-center size-8 [&>svg]:size-4 md:[&>svg]:size-5 md:size-10 rounded-md bg-mapiq-black-800*/[0.03] transition-colors mt-auto",
          {
            "bg-energizing-yellow-500*": workdayStatus === "OfficeDay",
          },
        )}
      />
      <span
        data-indicator
        className={cn(
          "absolute inset-x-0 inset-y-1 sm:-inset-x-3 md:-inset-x-4 sm:inset-y-0 border-transparent transition-colors z-[-1] pointer-events-none",
          {
            "border-2 border-mapiq-black-800* rounded-lg+ sm:rounded-none sm:border-none sm:bg-mapiq-black-800*/[0.03]":
              selectedDate === dayKey,
          },
        )}>
        <span
          className={cn("h-0 w-full bg-mapiq-black-800* transition-[height] absolute bottom-0 hidden sm:block", {
            "h-[2px]": selectedDate === dayKey,
          })}
        />
      </span>
    </button>
  );
};
